import React, { useState, useEffect, useCallback } from 'react';
import { IoIosArrowBack } from 'react-icons/io';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { IoMdCloseCircleOutline } from 'react-icons/io';
import Spinner from 'react-bootstrap/Spinner';
import { fetchWithAuth } from './authService';
import { API_ENDPOINTS } from '../utils/constants';

const Playlist = () => {
  const [searchParams] = useSearchParams();
  const [songs, setSongs] = useState([]);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [songCost, setSongCost] = useState(null);
  const [songToAdd, setSongToAdd] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [price, setPrice] = useState([]);
  const fetchPrice = async () => {
    try {
      const response = await fetchWithAuth(
        API_ENDPOINTS.ORDER_PRICE_BY_VENUE(localStorage.getItem('vId'))
      );
      if (!response.ok) throw new Error('Failed to fetch price');

      const data = await response.json();
      // Filter out hidden packs
      const visiblePacks = data.filter(pack => !pack.isHidden);
      setPrice(visiblePacks);
    } catch (error) {
      console.error('Error fetching prices:', error);
    }
  };
  useEffect(() => {
    fetchPrice();
  }, []);
  const cancelMsg = () => {
    setIsError(false);
  };
  const navigate = useNavigate();

  function goBack() {
    window.history.back();
  }
  useEffect(() => {
    if (isError) {
      const timeout = setTimeout(() => {
        cancelMsg(); // Set isError to false
      }, 4000);
      return () => clearTimeout(timeout);
    }
  }, [isError]);

  const loadMoreSongs = useCallback(async () => {
    if (isFetchingMore || !hasMore) return;
    const pid = searchParams.get('pid');
    if (!pid) return;
    const venueId = localStorage.getItem('venueId');
    setIsFetchingMore(true);
    try {
      const songsResponse = await fetchWithAuth(
        `${API_ENDPOINTS.PLAYLIST_TRACKS(pid)}?skip=${skip}&limit=${limit}${venueId ? `&venueId=${venueId}` : ''}`,
        {
          method: 'GET',
        }
      );
      if (!songsResponse.ok) throw new Error('Failed to fetch data');
      if (songsResponse.status === 204) {
        setHasMore(false);
        setIsFetchingMore(false);
        return;
      }
      const songsdata = await songsResponse.json();
      if (!Array.isArray(songsdata) || songsdata.length === 0) {
        setHasMore(false);
      } else {
        setSongs(prev => [...prev, ...songsdata]);
        setSkip(prev => prev + songsdata.length);
      }
    } catch (e) {
      console.log('Error :', e);
    } finally {
      setIsFetchingMore(false);
    }
  }, [hasMore, isFetchingMore, limit, searchParams, skip]);

  useEffect(() => {
    // initial load
    setSkip(0);
    setSongs([]);
    setHasMore(true);
  }, [searchParams]);

  useEffect(() => {
    if (songs.length === 0 && hasMore && !isFetchingMore) {
      loadMoreSongs();
    }
  }, [songs.length, hasMore, isFetchingMore, loadMoreSongs]);

  useEffect(() => {
    const handleScroll = () => {
      const nearBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 300;
      if (nearBottom && hasMore && !isFetchingMore) {
        loadMoreSongs();
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, isFetchingMore, loadMoreSongs]);
  const [profile, setProfile] = useState({});
  // Fetch profile with normal fetch
  const fetchProfile = useCallback(async () => {
    const token = localStorage.getItem('tringAuthToken');
    if (!token) {
      navigate('/');
      return;
    }

    try {
      const response = await fetchWithAuth(API_ENDPOINTS.USERS, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch data');

      const data = await response.json();
      setProfile({
        coins: data?.coins ?? 0,
        username: data?.username ?? '',
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile({ coins: 0, username: '' });
      setErrorMsg('Failed to load profile');
      setIsError(true);
    }
  }, [navigate]);
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);
  const openConfirm = useCallback(
    async song => {
      const venueId = localStorage.getItem('vId');
      const cost = localStorage.getItem('songCost');

      if (!venueId) {
        setIsError(true);
        setErrorMsg('No Venue Selected..');
        return;
      }

      if (!cost) {
        setErrorMsg('Invalid song cost or DJ details');
        setIsError(true);
        return;
      }

      const isKJ = localStorage.getItem('isKJ');
      if (isKJ === 'true') {
        const token = localStorage.getItem('tringAuthToken');
        const venueId = localStorage.getItem('venueId');

        try {
          const res = await fetch(
            API_ENDPOINTS.VENUE_SLOTS_AVAILABILITY(venueId),
            {
              method: 'GET',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            }
          );

          if (!res.ok) {
            setIsError(true);
            setErrorMsg('Failed to check song slot availability.');
            return;
          }

          const data = await res.json();
          if (!data.available) {
            setErrorMsg('No more song slots available!');
            setIsError(true);
            return;
          }
        } catch (error) {
          console.error('Error fetching data:', error);
          setIsError(true);
          setErrorMsg('Network error occurred. Please try again.');
          return;
        }
      }

      setSongCost(cost);
      setSongToAdd(song);
      console.log('song', song);
      const currentCoins = profile?.coins || 0;

      if (currentCoins < Number(cost)) {
        navigate('/insufficient-coins', {
          state: {
            song,
            cost,
            priceData: price,
            walletCoins: currentCoins,
          },
        });
      } else {
        setConfirmModal(true);
      }
    },
    [profile]
  );

  const cancelConfirm = song => {
    setSongToAdd([]);
    setConfirmModal(false);
  };

  const onAddSong = async (trackId, songName) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('tringAuthToken');
      if (!token) {
        console.error('No auth token found');
        return;
      }

      const venueId = localStorage.getItem('venueId');
      if (!venueId) {
        setIsLoading(false);
        setConfirmModal(false);
        setIsError(true);
        setErrorMsg('No Venue Selected..');
        return;
      }
      if (localStorage.getItem('shoutout') === 'true') {
        localStorage.setItem('shouttrackId', trackId);
        navigate('/shoutout');

        return;
      }

      if (localStorage.getItem('shoutout') === 'true') {
        localStorage.setItem('shouttrackId', trackId);

        return;
      }

      // console.log(venueId);
      // console.log(token);
      // console.log(trackId);

      const addResponse = await fetchWithAuth(
        API_ENDPOINTS.VENUE_QUEUE(venueId),
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            trackId: trackId,
          }),
        }
      );

      if (!addResponse.ok) {
        const errorData = await addResponse.json(); // try to parse the error message
        const errormsg =
          errorData?.message || errorData?.error || 'Something went wrong';
        setErrorMsg(errormsg.toString());
        setIsLoading(false);
        setIsError(true);
        setConfirmModal(false);
        return;
      }
      if (addResponse.status === 204) {
        console.log('Request was successful, but no content returned.');
        localStorage.setItem('notif', true);
        setConfirmModal(false);
        setIsLoading(false);
        setSongToAdd([]);
        // alert("Successfully requested Song");
        navigate(`/venues/${venueId}`);
        return;
      }
    } catch (error) {
      console.error('Error requesting songs:', error);
      alert(error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('tringAuthToken');
    if (!token) {
      console.error('No auth token found');
      navigate('/');
      return;
    }
  }, [navigate]);

  const excludedLabelsRaw = localStorage.getItem('excludedLabels');
  let excludedLabels = [];

  if (excludedLabelsRaw) {
    try {
      excludedLabels = JSON.parse(excludedLabelsRaw);
    } catch {
      excludedLabels = excludedLabelsRaw.split(',').map(l => l.trim());
    }
  }

  return (
    <div className="w-100 px-2 d-flex flex-column" style={{ height: '100dvh' }}>
      {confirmModal ? (
        <div className="modale">
          {/* Overlay */}
          <div
            className="overlay"
            onClick={() => cancelConfirm()}
            style={{
              position: 'fixed',
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 999,
            }}
          />

          {/* Modal content */}
          <div
            className="modale-content"
            style={{
              position: 'fixed',
              top: '40%', // slightly above center
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
              borderRadius: '15px',
              overflow: 'hidden',
              maxWidth: '90%',
              background: '#fff',
            }}
          >
            <div
              className="w-100 p-3 d-flex flex-row"
              style={{
                borderRadius: '15px 15px 0px 0px',
                background: 'linear-gradient(90deg, #C91721 0%, #8A325D 100%)',
              }}
            >
              <div className="col-3"></div>
              <div className=" col-9 d-flex flex-row justify-content-between">
                <h4 className="m-0" style={{ color: 'white' }}>
                  Song Request
                </h4>
                <button
                  className=""
                  style={{
                    color: 'white',
                    background: 'transparent',
                    border: 'none',
                  }}
                  onClick={() => cancelConfirm()}
                >
                  <IoMdCloseCircleOutline
                    style={{ color: 'white', fontSize: '25px' }}
                  />{' '}
                </button>
              </div>
            </div>

            {songToAdd ? (
              <div className="w-100">
                <div
                  className="px-3 w-100"
                  style={{ background: '#FFF', padding: '15px 0px 15px 0px' }}
                >
                  <div className="d-flex flex-row justify-content-between align-items-center">
                    <div
                      className="d-flex flex-row align-items-center"
                      style={{ gap: '15px' }}
                    >
                      <img
                        src={songToAdd.album.images[0].url}
                        style={{
                          height: '60px',
                          width: '60px',
                          borderRadius: '50%',
                        }}
                      />
                      <h4
                        style={{
                          color: '#9F2A4A',
                          fontSize: 'clamp(0px,4vw,16px)',
                        }}
                        className="text-start col-8"
                      >
                        {songToAdd.name}
                      </h4>
                    </div>

                    {localStorage.getItem('shoutout') !== 'true' && (
                      <div>
                        <img
                          src="/images/coin.svg"
                          style={{
                            width: 'clamp(0px,5vw,20px)',
                            aspectRatio: '1/1',
                          }}
                        />
                        <h4
                          style={{
                            color: '#9F2A4A',
                            fontSize: 'clamp(0px,4vw,16px)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {songCost} coin
                        </h4>
                      </div>
                    )}
                  </div>
                </div>

                <div className="d-flex flex-row justify-content-between align-items-center w-100">
                  <button
                    className="col-6 p-2"
                    style={{
                      background: '#FFF',
                      color: '#C71823',
                      border: 'solid .5px #C71823',
                      borderRadius: '0px 0px 0px 15px',
                      fontSize: '18px',
                    }}
                    onClick={() => cancelConfirm()}
                  >
                    Cancel
                  </button>

                  <button
                    className="col-6 p-2"
                    style={{
                      background:
                        'linear-gradient(90deg, #CC151E -18.35%, #873662 108.23%)',
                      color: 'white',
                      border: 'solid .5px #C71823',
                      borderRadius: '0px 0px 15px 0px',
                      fontSize: '18px',
                    }}
                    onClick={() => onAddSong(songToAdd.id, songToAdd.name)}
                  >
                    Confirm
                  </button>
                </div>
              </div>
            ) : (
              <div>Just a sec</div>
            )}
          </div>
        </div>
      ) : (
        <div></div>
      )}

      {isError && (
        <div
          style={{
            position: 'fixed',
            bottom: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            color: '#FFFFFF',
            background: 'rgb(32, 32, 32)',
            borderRadius: '4px',
            boxShadow: '0 4px 12px rgba(32, 32, 32, 0.73)',
            padding: '16px 24px',
            minWidth: '95vw',
            maxWidth: '95vw',
            animation: 'slideDown 0.4s cubic-bezier(0.18, 0.89, 0.32, 1.28)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <span
            style={{
              flex: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'start',
            }}
          >
            {typeof errorMsg === 'string'
              ? errorMsg
              : errorMsg?.message || 'Something went wrong'}
          </span>
          {errorMsg === 'No Venue Selected..' && (
            <button
              style={{
                flexShrink: 0,
                padding: '4px 8px',
                backgroundColor: 'rgb(211, 64, 64)',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                fontSize: '13px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
              onClick={() => {
                localStorage.setItem('mainInd', 1);
                window.location.reload();
              }}
            >
              Select Venue
            </button>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="lmodale">
          <div className="loverlay"></div>

          <div className="lmodalecontent">
            <Spinner animation="border" role="status" style={{ color: 'red' }}>
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        </div>
      ) : (
        <div></div>
      )}

      <div
        className="d-flex flex-row align-items-center w-100 my-3"
        style={{
          color: '#D0151C',
          background: 'linear-gradient(90deg, #641FAA 0%, #8A325D 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        <IoIosArrowBack
          className="mt-1"
          style={{ fontSize: '22px', marginRight: '10px', color: '#8A325D' }}
          onClick={() => goBack()}
        />
        <h2 className="m-0">{localStorage.getItem('playlistname')}</h2>
      </div>

      <div className="w-100">
        <img
          className="col-6 my-1"
          src={localStorage.getItem('playlistImg')}
          style={{ borderRadius: '13px', aspectRatio: '1/1' }}
        />
      </div>

      <div className="mt-3" style={{ scrollBehavior: 'smooth' }}>
        {songs.map((song, index) => {
          const isExcluded =
            song?.label && excludedLabels?.includes(song.label);
          return (
            <div
              key={index}
              onClick={() => {
                if (isExcluded) {
                  setErrorMsg('This song is available in this venue.');
                  setIsError(true);
                } else {
                  openConfirm(song);
                }
              }}
            >
              <div className="mb-3">
                <div className="d-flex flex-row justify-content-between align-items-center">
                  <img
                    className="mx-2"
                    style={{
                      height: '55px',
                      width: '55px',
                      borderRadius: '50%',
                    }}
                    src={song.album.images[0].url}
                  />

                  <div
                    className="d-flex flex-column mx-2 text-start overflow-hidden"
                    style={{ gap: '5px', width: '50%' }}
                  >
                    <h5
                      className="text-start col-6 mb-0"
                      style={{
                        fontSize: 'clamp(0px,4vw,18px)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {song?.name}
                    </h5>
                    <div
                      className="w-100 overflow-hidden d-flex flex-row justify-content-start align-items-center"
                      style={{ gap: '5px' }}
                    >
                      {song?.artists.slice(0, 2).map((artist, index) => (
                        <span
                          key={index}
                          className="text-start overflow-hidden common-txt"
                          style={{
                            fontSize: 'clamp(0px,3.3vw,15px)',
                            whiteSpace: 'nowrap',
                            color: 'gray',
                            maxWidth: '40%',
                          }}
                        >
                          {artist?.name}
                        </span>
                      ))}
                      {song?.artists?.length > 2 && (
                        <div
                          className=""
                          style={{
                            aspectRatio: '1/1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 'clamp(0px,3.3vw,15px)',
                            color: 'gray',
                          }}
                        >
                          +{song?.artists?.length - 2}
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    className="mx-2 px-3 py-2 my-2"
                    style={{
                      color: 'white',
                      border: 'none',
                      background: isExcluded
                        ? 'gray'
                        : 'linear-gradient(90deg, #D51217 0%, #843866 100%)',
                      height: 'fit-content',
                      fontSize: 'clamp(0px,3.3vw,15px)',
                      whiteSpace: 'nowrap',
                      borderRadius: '15px',
                      cursor: isExcluded ? 'not-allowed' : 'pointer',
                      opacity: isExcluded ? 0.6 : 1,
                    }}
                    onClick={() => {
                      if (isExcluded) {
                        setErrorMsg('This song is available in this venue.');
                        setIsError(true);
                      } else {
                        openConfirm(song);
                      }
                    }}
                  >
                    + Add
                  </button>
                </div>
                <hr />
              </div>
            </div>
          );
        })}
        {isFetchingMore && (
          <div className="d-flex justify-content-center my-3">
            <Spinner animation="border" role="status" style={{ color: 'red' }}>
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        )}
      </div>
    </div>
  );
};

export default Playlist;
